import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { UserManager } from "oidc-client";
import oidcConfig from "../../config/oidc-config";
import { formatToMMDDYYYY, smartTrim, trimOnBlur } from "../../utils/Utils";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { ApplicationApi } from "../../service/Api/ApplicationsApi";
const userManager = new UserManager(oidcConfig);

async function getAccessToken() {
  try {
    const user = await userManager.getUser();
    if (!user) {
      throw new Error("User is not authenticated");
    }
    return user.access_token;
  } catch (error) {
    console.error("Error fetching access token:", error);
    throw error;
  }
}
const PatientSearch = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [localPageSize, setLocalPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isPageSizeChange, setIsPageSizeChange] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [isClear, setIsClear] = useState(false);
  const navigate = useNavigate();
  const [searchCriteria, setSearchCriteria] = useState({
    MemberID: "",
    FirstName: "",
    LastName: "",
    GivenName: "",
    UserName: "",
    Email: "",
    DOB: "",
    RowStatus: "",
    BB_IsActive: "",
    BB_MemberID: "",
  });
  const [serchData, setSearchData] = useState({
    firstName: "",
    lastName: "",
    memberID: "",
    dob: "",
    email: "",
  });
  const [tempDate, setTempDate] = useState(null);
  const openNewTabWithUrl = (SSOGuid) => {
    const impersonateURL = process.env.REACT_APP_Impersonate_URL;
    const url = `${impersonateURL}${SSOGuid}`;
    window.open(url, "_blank");
  };
  const fetchIdRef = React.useRef(0);
  const columns = useMemo(
    () => [
      {
        Header: "First Name",
        accessor: "firstName",
      },
      {
        Header: "Last Name",
        accessor: "lastName",
      },
      {
        Header: "DOB",
        accessor: "dob",
        Cell: ({ value }) => (value ? formatToMMDDYYYY(value) : "-"),
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Last Login Date",
        accessor: "lastLoginDatetime",
        Cell: ({ value }) => (value ? formatToMMDDYYYY(value) : "-"),
      },
      {
        Header: "Member ID",
        accessor: "memberID",
        Cell: ({ value }) => (value ? value : "-"),
      },
      {
        Header: "Enable Blue Button",
        accessor: "bB_IsActive",
      },
      {
        Header: "View Profile",
        Cell: ({ row }) => (
          <i
            className="fh_open_in_new fs-4 primary cursor-pointer react-tooltip"
            data-tooltip-id="viewPatient"
            data-tooltip-content="View Patient Profile"
            data-tooltip-place="top"
            onClick={() => openNewTabWithUrl(row.original.ssoGuid)}
          ></i>
        ),
        disableSortBy: true,
      },
    ],
    []
  );
  const fetchPaginatedData = async () => {
    try {
      let accessToken = "";
      const PortalToken = localStorage.getItem("PortalAdmin-AccessToken");
      if (PortalToken) {
        accessToken = localStorage.getItem("PortalAdmin-AccessToken");
      } else {
        accessToken = await getAccessToken();
      }
      // Extracting sort parameters
      let sortParam = sortBy.length > 0 ? sortBy[0].id : null;
      let sortDirection = sortBy.length > 0 && sortBy[0].desc ? "DESC" : "ASC";

      let filteredCriteria = {};

      let queryParams = new URLSearchParams({
        page: pageIndex + 1,

        ...filteredCriteria,
      });
      if (sortParam) {
        queryParams.append("sort", sortParam);
        queryParams.append("sortDirection", sortDirection);
      }
      const response = await ApplicationApi.getPayerMemeberList();
      return response;
    } catch (error) {
      console.error("Error fetching patient data:", error);
      return { data: [], pageInfo: { totalCount: 0 } };
    }
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: currentPage, sortBy: [] },
      manualPagination: true,
      manualSortBy: true,
      pageCount: pageCount,
    },
    useSortBy,
    usePagination
  );
  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    const fetchId = ++fetchIdRef.current;

    try {
      try {
      } catch (error) {
        if (error.response && error.response.status === 404) {
          navigate("/login");
        } else {
          console.error("Failed to fetch data:", error);
        }
      }
      const responseData = await fetchPaginatedData();
      if (fetchId === fetchIdRef.current) {
        setData(responseData?.data || []);
        setPageCount(responseData.totalPages || 0);
        await setCurrentPage(responseData?.pageNumber ?? 0);

        setTotalRecords(responseData.totalCount || 0);
        setHasNextPage(responseData.hasNextPage ?? false);
        setHasPreviousPage(responseData.hasPreviousPage ?? false);
      }
    } catch (error) {
      console.error("Error fetching patient data:", error);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    const isCriteriaCleared = Object.values(searchCriteria).every(
      (value) => value === ""
    );
    if (isCriteriaCleared) {
      fetchData({
        pageSize: localPageSize,
        pageIndex: currentPage,
        sortBy,
        criteria: {},
      });
    }
  }, []);
  const handleClear = async () => {
    setSearchData(
      {
        memberID: "",
        firstName: "",
        lastName: "",
        email: "",
        dob: "",
      },
      setTempDate(""),

      await fetchData({
        pageSize: localPageSize,
        pageIndex: 0,
        sortBy,
        criteria: {},
      })
    );
    setIsClear(true);
    setLocalPageSize(10);
  };

  const trimSearchFields = (data) => ({
    ...data,
    firstName: trimOnBlur(data.firstName),
    lastName: trimOnBlur(data.lastName),
    email: trimOnBlur(data.email),
    memberID: trimOnBlur(data.memberID),
  });

  const handleSearchSubmit = async (nextSearchData) => {
    const source =
      nextSearchData &&
      typeof nextSearchData === "object" &&
      "firstName" in nextSearchData
        ? nextSearchData
        : serchData;
    const payload = trimSearchFields(source);
    setSearchData(payload);
    setLoading(true);
    try {
      const response = await ApplicationApi.getPayerMemeberList(
        payload.memberID,
        payload.firstName,
        payload.lastName,
        payload.dob,
        payload.email
      );
      if (response && response.data) {
        const fetchedData = response.data;
        setData(fetchedData);
        setTotalRecords(response.totalCount);
        setPageCount(response.totalPages);
        setHasNextPage(response.hasNextPage);
        setHasPreviousPage(response.hasPreviousPage);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Error fetching member list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setTempDate(date);

    const formattedDate = formatDateForQuery(date);
    setSearchData({
      ...serchData,
      dob: formattedDate,
    });
  };

  const formatDateForQuery = (date) => {
    if (date instanceof Date && !isNaN(date)) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      // return `${year}-${month}-${day}`;
      return `${month}-${day}-${year}`;
    }
    return "";
  };

  const handleChangePageSize = async (newPageSize) => {
    setIsPageSizeChange(true);
    setLocalPageSize(newPageSize);
    const newTotalPages = Math.ceil(totalRecords / newPageSize);
    let newCurrentPage =
      currentPage >= newTotalPages ? newTotalPages - 1 : currentPage;
    if (isPageSizeChange) {
      newCurrentPage = 1;
    }
    setCurrentPage(newCurrentPage);
    try {
      const payload = trimSearchFields(serchData);
      const response = await ApplicationApi.getPayerMemeberList(
        payload.memberID,
        payload.firstName,
        payload.lastName,
        payload.dob,
        payload.email,
        newCurrentPage,
        newPageSize
      );
      if (response && response.data) {
        setData(response.data);
        setTotalRecords(response.totalCount);
        setPageCount(response.totalPages);
        setCurrentPage(response.pageNumber);
        setHasNextPage(response.hasNextPage);
        setHasPreviousPage(response.hasPreviousPage);
      }
    } catch (error) {
      console.error("Error fetching paginated data:", error);
    }
  };

  const handleNext = async () => {
    if (hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchPageData(nextPage);
    }
  };

  const handlePrevious = async () => {
    setCurrentPage(currentPage - 1);
    fetchPageData(currentPage - 1);
  };
  const handleGoToFirstPage = async () => {
    setCurrentPage(1);
    fetchPageData(1);
  };
  const handleGoToLastPage = async () => {
    setCurrentPage(pageCount);
    fetchPageData(pageCount);
  };

  const fetchPageData = async (page) => {
    try {
      const payload = trimSearchFields(serchData);
      const response = await ApplicationApi.getPayerMemeberList(
        payload.memberID,
        payload.firstName,
        payload.lastName,
        payload.dob,
        payload.email,
        page,
        localPageSize
      );
      if (response && response.data) {
        setData(response?.data);
        setHasNextPage(response.hasNextPage);
        setHasPreviousPage(response.hasPreviousPage);
        setCurrentPage(response.pageNumber);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };
  useEffect(() => {
    if (serchData.firstName.trim() === "") {
      const timeout = setTimeout(() => {
        handleSearchSubmit();
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [serchData.firstName]);

  useEffect(() => {
    if (serchData.lastName.trim() === "") {
      const timeout = setTimeout(() => {
        handleSearchSubmit();
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [serchData.lastName]);

  useEffect(() => {
    if (serchData.email.trim() === "") {
      const timeout = setTimeout(() => {
        handleSearchSubmit();
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [serchData.email]);

  useEffect(() => {
    if (serchData.memberID.trim() === "") {
      const timeout = setTimeout(() => {
        handleSearchSubmit();
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [serchData.memberID]);

  useEffect(() => {
    if (tempDate === null) {
      const timeout = setTimeout(() => {
        handleSearchSubmit();
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [tempDate]);
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const currentUser = await userManager.getUser();

        if (currentUser && !currentUser.expired) {
          // Make the API call to get user info based on their sub (SSO ID)
          const userResponse = await ApplicationApi.getUserBySSOID(currentUser.profile.sub);

          // If no user info or invalid role, redirect to login page
          if (!userResponse || !userResponse.roleID) {
            navigate("/login");
            return;
          }
        } 
      } catch (error) {
        console.error("Error fetching user info:", error);
        
      }
    };

    fetchUserInfo();
  }, [navigate]);
  return (
    <div className="main-content">
      <div className="content-header">
        <h1 className="page-header">Patient Search</h1>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="card-form form-content mt-0">
            <div className="row">
              <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                <label htmlFor="FirstName" className="d-flex form-label">
                  First Name
                </label>
                <input
                  type="text"
                  name="FirstName"
                  className="form-text form-control"
                  value={serchData.firstName}
                  onChange={(e) => {
                    setSearchData({
                      ...serchData,
                      firstName: smartTrim(e.target.value),
                    });
                  }}
                  onBlur={(e) => {
                    setSearchData({
                      ...serchData,
                      firstName: trimOnBlur(e.target.value),
                    });
                  }}
                  placeholder="Enter First Name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchSubmit({
                        ...serchData,
                        firstName: trimOnBlur(e.target.value),
                      });
                    }
                  }}
                />
              </div>
              <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                <label htmlFor="FirstName" className="d-flex form-label">
                  Last Name
                </label>
                <input
                  type="text"
                  name="LastName"
                  className="form-text form-control"
                  value={serchData.lastName}
                  onChange={(e) => {
                    setSearchData({
                      ...serchData,
                      lastName: smartTrim(e.target.value),
                    });
                  }}
                  onBlur={(e) => {
                    setSearchData({
                      ...serchData,
                      lastName: trimOnBlur(e.target.value),
                    });
                  }}
                  placeholder="Enter Last Name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchSubmit({
                        ...serchData,
                        lastName: trimOnBlur(e.target.value),
                      });
                    }
                  }}
                />
              </div>
              <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                <label htmlFor="DOB" className="d-flex form-label">
                  Date of Birth
                </label>
                <div className="form-field position-relative">
                  <DatePicker
                    id="DOB"
                    className={`form-control h-48`}
                    placeholderText="MM/DD/YYYY"
                    selected={tempDate}
                    onChange={(date) => {
                      handleDateChange(date);
                    }}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                <label htmlFor="Email" className="d-flex form-label">
                  Email
                </label>
                <input
                  type="text"
                  name="Email"
                  className="form-text form-control"
                  value={serchData.email}
                  onChange={(e) => {
                    setSearchData({
                      ...serchData,
                      email: smartTrim(e.target.value),
                    });
                  }}
                  onBlur={(e) => {
                    setSearchData({
                      ...serchData,
                      email: trimOnBlur(e.target.value),
                    });
                  }}
                  placeholder="Enter Email"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchSubmit({
                        ...serchData,
                        email: trimOnBlur(e.target.value),
                      });
                    }
                  }}
                />
              </div>
              <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                <label htmlFor="MemberID" className="d-flex form-label">
                  Member ID
                </label>
                <input
                  type="text"
                  name="MemberID"
                  className="form-text form-control"
                  value={serchData.memberID}
                  onChange={(e) => {
                    setSearchData({
                      ...serchData,
                      memberID: smartTrim(e.target.value),
                    });
                  }}
                  onBlur={(e) => {
                    setSearchData({
                      ...serchData,
                      memberID: trimOnBlur(e.target.value),
                    });
                  }}
                  placeholder="Enter Member ID"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchSubmit({
                        ...serchData,
                        memberID: trimOnBlur(e.target.value),
                      });
                    }
                  }}
                />
              </div>
            </div>
            <div className="col-12 text-center">
              <button
                type="button"
                data-tooltip-id="Aboutme"
                data-tooltip-content="About me"
                data-tooltip-place="right"
                className="btn btn-primary h-48 me-3"
                onClick={() => handleSearchSubmit()}
                disabled={
                  !serchData.firstName &&
                  !serchData.lastName &&
                  !serchData.dob &&
                  !serchData.email &&
                  !serchData.memberID
                }
              >
                Search
              </button>
              <button
                type="button"
                className="btn btn-border-primary h-48"
                onClick={handleClear}
                disabled={
                  !serchData.firstName &&
                  !serchData.lastName &&
                  !serchData.dob &&
                  !serchData.email &&
                  !serchData.memberID
                }
              >
                Clear
              </button>
            </div>
          </div>
          {/* Table for displaying data */}
          <div className="card-content">
            <div className="content-subheader d-flex align-items-center">
              <h2 className="page-subheader me-3">Patient List </h2>
              <span className="total-record">
                [ Total Patients: {totalRecords} ]
              </span>
            </div>
            <div className="table-responsive">
              <Tooltip anchorSelect=".react-tooltip" />
              <table {...getTableProps()} className="table caption-top">
                <thead>
                  {/* Render table headers */}
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th>
                          {column.render("Header")}
                          {/* Sort direction indicators */}
                          <span className="ms-2 position-relative top-2"></span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {/* Conditional rendering for loading state and no data */}
                  {loading ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center my-4">
                        <div className="spinner-border" role="status"></div>
                      </td>
                    </tr>
                  ) : data.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center">
                        No data found
                      </td>
                    </tr>
                  ) : (
                    page.map((row) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {data && data.length > 0 && (
            <div className="d-flex justify-content-center">
              <nav
                aria-label="Page navigation"
                className="d-flex align-items-center"
              >
                <span className="pagnination-info me-2">
                  Page{" "}
                  <strong>
                    {currentPage ? currentPage : 1} of {pageCount}
                  </strong>{" "}
                </span>
                <ul className="pagination d-flex justify-content-end mb-0">
                  <li className="page-item">
                    <button
                      className="page-link"
                      onClick={handleGoToFirstPage}
                      disabled={!hasPreviousPage}
                    >
                      <i className="fh_double_arrow_left"></i>
                    </button>
                  </li>
                  <li className="page-item">
                    <button
                      className="page-link"
                      onClick={handlePrevious}
                      disabled={!hasPreviousPage}
                    >
                      <i className="fh_arrow_left_line"></i>
                    </button>
                  </li>
                  <li className="page-item"></li>
                  <li className="page-item">
                    <button
                      className="page-link"
                      onClick={handleNext}
                      disabled={!hasNextPage}
                    >
                      <i className="fh_arrow_right_line"></i>
                    </button>
                  </li>
                  <li className="page-item">
                    <button
                      className="page-link"
                      onClick={handleGoToLastPage}
                      disabled={!hasNextPage}
                    >
                      <i className="fh_double_arrow_right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
              <div>
                <select
                  className="form-select form-control cursor-pointer custom-table-dropdown"
                  value={localPageSize}
                  onChange={(e) => handleChangePageSize(Number(e.target.value))}
                >
                  {[10, 20, 30].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default PatientSearch;
